import React from "react";
import ImageComponent from "theme/components/atoms/Image";
import Link from "theme/components/atoms/Typography/Link";
import classNames from "classnames";
import MediaQuery from "theme/components/helpers/MediaQuery";
import Breakpoints from "theme/components/atoms/Breakpoints";
import PropTypes from "prop-types";
import { presets, originalPresetCode } from "config/images";

const DEFAULT_PRESET_CODE = originalPresetCode || "original";

/**
 * @param {object} props
 * @param {string} props.src
 * @param {string} props.alt
 * @param {string} props.format
 * @param {string} props.title
 * @returns
 */
const Picture = (props) => {
  if (props.format === DEFAULT_PRESET_CODE) {
    return (
      <img
        src={props.src + `?format=${DEFAULT_PRESET_CODE}`}
        alt={props.altText}
        title={props.title}
        loading="auto"
      />
    );
  }

  return (
    <ImageComponent
      src={props.src}
      alt={props.altText}
      format={props.format}
      title={props.title}
      appearance="full"
    />
  );
};

/** @type {React.FC<ImageProps>}*/
const Image = ({ data, className, "data-pb-style": styleId }) => {
  if (!data.desktopImage && !data.mobileImage) {
    if (process.env.NODE_ENV === "development") {
      throw new Error("Image component requires at least one src image");
    }
    return null;
  }

  const PictureFragment = (
    <>
      <MediaQuery minWidth={Breakpoints.desktop}>
        <Picture
          src={data.desktopImage}
          alt={data.altText}
          format={data.desktopFormat}
          title={data.title}
        />
      </MediaQuery>
      <MediaQuery maxWidth={Breakpoints.desktop - 1}>
        <Picture
          src={data.mobileImage}
          alt={data.altText}
          format={data.mobileFormat}
          title={data.title}
        />
      </MediaQuery>
    </>
  );

  if (data.link?.href && data.link?.type === "default") {
    return (
      <figure
        className={classNames("pagebuilder-image", className)}
        data-pb-style={styleId}
      >
        <Link external to={data.link.href}>
          {PictureFragment}
        </Link>
        {data?.caption && <figcaption>{data.caption}</figcaption>}
      </figure>
    );
  }

  return (
    <figure
      className={classNames("pagebuilder-image", className)}
      data-pb-style={styleId}
    >
      {PictureFragment}
      {data?.caption && <figcaption>{data.caption}</figcaption>}
    </figure>
  );
};

Image.defaultProps = {
  data: {
    altText: "",
    link: {
      href: "",
      type: "",
    },
  },
};

const imageFormats = [...Object.keys(presets), DEFAULT_PRESET_CODE];

/** @typedef {PropTypes.InferProps<typeof propTypes>} ImageProps */
const propTypes = {
  data: PropTypes.shape({
    desktopImage: PropTypes.string.isRequired,
    desktopFormat: PropTypes.oneOf(imageFormats).isRequired,
    mobileImage: PropTypes.string.isRequired,
    mobileFormat: PropTypes.oneOf(imageFormats).isRequired,
    altText: PropTypes.string,
    title: PropTypes.string,
    caption: PropTypes.string,
    link: PropTypes.shape({
      href: PropTypes.string,
      type: PropTypes.oneOf(["default", "page", "category", "product"]),
    }),
  }),
  className: PropTypes.string,
};

Image.propTypes = propTypes;

export default Image;
